import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import PanelShell from './PanelShell';
import { RiDeleteBinLine, RiFileCopyLine, RiPencilLine } from 'react-icons/ri';
import {
  ReactFlow,
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useModelSettings } from '../hooks/useModelSettings';
import { useWorkflow } from '../hooks/useWorkflow';
import { useIsMobile } from '../hooks/useIsMobile';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { useModelSlots } from '../hooks/useModelSlots';
import { useUserRuns } from '../hooks/useUserRuns';
import WorkflowNodeConfig, {
  NODE_COLORS,
} from './subcomponents/WorkflowNodeConfig';
import WorkflowRunHistory from './subcomponents/WorkflowRunHistory';
import WorkflowTutorialContent from './content/diagrams/WorkflowTutorialContent';
import '../styles/workflow.css';

import {
  NodeStatusCtx,
  dagToFlow,
  flowToDag,
  defaultConfig,
  NODE_TYPES,
  EDGE_TYPES,
  NODE_TYPE_LABELS,
} from './workflowBuilderNodes';
import { WorkflowList } from './workflowBuilderList';
import {
  RunStatusBadge,
  LiveRunOutput,
  RunInputDialog,
} from './workflowBuilderRunOutput';

/* ─── Workflow limits (must stay in sync with _MAX_DAG_NODES in
       stackform/orchestrator/orchestrator/routers/workflow.py) ── */
const MAX_WORKFLOW_NODES = 20;


const PALETTE_ITEMS = [
  { nodeType: 'input',  label: 'Input',  requiresProvider: false, requiresTool: false },
  { nodeType: 'output', label: 'Output', requiresProvider: false, requiresTool: false },
  { nodeType: 'agent',  label: 'Agent',  requiresProvider: true,  requiresTool: false },
  /* Tool nodes are always addable — ddg_search and arxiv_search need
   * no API keys.  Per-node warnings in the config panel handle the
   * case where tavily_search / you_search is selected without a key. */
  { nodeType: 'tool',   label: 'Tool',   requiresProvider: false, requiresTool: false },
  { nodeType: 'logic',  label: 'Logic',  requiresProvider: false, requiresTool: false },
];

/* ─── Inner canvas component (needs ReactFlow context) ───────── */

function CanvasInner({
  activeWorkflow,
  activeWorkflowId,
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setNodes,
  setEdges,
  selectedNode,
  setSelectedNode,
  onNodeUpdate,
  onNodeDelete,
  pushHistory,
  floatingBar,
  onNodeFocus,
}) {
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition } = useReactFlow();

  const onConnect = useCallback(
    (params) => {
      pushHistory();
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            id: crypto.randomUUID(),
            type: 'deletable',
          },
          eds,
        ),
      );
      /* When connecting to an output node, sync its variable_name
         from the source node's output_variable automatically. */
      setNodes((nds) => {
        const target = nds.find((n) => n.id === params.target);
        if (target?.data?.config?.node_type !== 'output') return nds;
        const src = nds.find((n) => n.id === params.source);
        const srcVar = src?.data?.config?.output_variable;
        if (!srcVar) return nds;
        return nds.map((n) =>
          n.id === params.target
            ? {
                ...n,
                data: {
                  ...n.data,
                  config: {
                    ...n.data.config,
                    variable_name: srcVar,
                  },
                },
              }
            : n,
        );
      });
    },
    [setEdges, setNodes, pushHistory],
  );

  const isValidConnection = useCallback(
    (connection) => {
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (targetNode?.data?.config?.node_type === 'output') {
        const alreadyHasEdge = edges.some(
          (e) => e.target === connection.target,
        );
        if (alreadyHasEdge) return false;
      }
      return true;
    },
    [nodes, edges],
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const nodeType = e.dataTransfer.getData(
        'application/wf-node-type',
      );
      if (!nodeType) return;
      if (nodes.length >= MAX_WORKFLOW_NODES) return;

      // Allowlist node types to prevent malformed DAG entries via
      // crafted DataTransfer objects.
      if (!['input', 'output', 'agent', 'tool', 'logic'].includes(nodeType)) return;

      const position = screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      pushHistory();
      const id = `node-${crypto.randomUUID().slice(0, 8)}`;
      const newNode = {
        id,
        type: `${nodeType}Node`,
        position,
        data: {
          label: NODE_TYPE_LABELS[nodeType] || nodeType,
          config: defaultConfig(nodeType),
        },
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [nodes.length, screenToFlowPosition, setNodes, pushHistory],
  );

  const onNodeClick = useCallback(
    (_evt, node) => {
      setSelectedNode(node);
      onNodeFocus?.();
    },
    [setSelectedNode, onNodeFocus],
  );

  const onPaneClick = useCallback(
    () => setSelectedNode(null),
    [setSelectedNode],
  );

  const nodesWithSelection = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        selected: n.id === selectedNode?.id,
      })),
    [nodes, selectedNode],
  );

  return (
    <div
      className="wf-canvas-wrap"
      style={{ position: 'relative' }}
      ref={reactFlowWrapper}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <ReactFlow
        nodes={nodesWithSelection}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        fitView
        deleteKeyCode="Delete"
        multiSelectionKeyCode="Shift"
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={18}
          size={1}
          color="var(--border-color)"
        />
        <Controls position="bottom-right" />
        <MiniMap
          position="bottom-left"
          nodeColor={(n) => {
            const nt = n.type?.replace('Node', '') || 'input';
            return NODE_COLORS[nt] || '#94a3b8';
          }}
          style={{
            background: 'var(--panel-background)',
            border: '1px solid var(--border-color)',
          }}
          maskColor="rgba(0,0,0,0.08)"
        />
      </ReactFlow>
      {floatingBar}
    </div>
  );
}

/* ─── Node palette & counter ─────────────────────────────────── */

/**
 * Compact counter chip showing current node count against the limit.
 * Color shifts from neutral → amber → red as the canvas fills up.
 */
function NodeCounterChip({ nodeCount, maxNodes }) {
  const pct = nodeCount / maxNodes;
  const color =
    pct >= 1
      ? 'var(--error-text-color, #e53e3e)'
      : pct >= 0.75
        ? 'var(--warning-color, #d97706)'
        : 'var(--text-secondary)';
  return (
    <span
      title={
        nodeCount >= maxNodes
          ? `Node limit reached. Delete a node to add another.`
          : `${maxNodes - nodeCount} node${maxNodes - nodeCount === 1 ? '' : 's'} remaining`
      }
      style={{
        fontSize: '0.72rem',
        fontWeight: 600,
        color,
        background: 'var(--bg-secondary, rgba(0,0,0,0.08))',
        borderRadius: 99,
        padding: '1px 7px',
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
        flexShrink: 0,
      }}
    >
      {nodeCount}/{maxNodes}
    </span>
  );
}

/**
 * Node palette listing draggable (desktop) or tappable (mobile)
 * node type items.
 *
 * Items that require missing API keys are rendered greyed-out with a
 * tooltip explaining what is needed.
 *
 * @param {function|undefined} onAddNode - Called with nodeType string.
 * @param {boolean} isMobile - Disables drag; shows tap "+" affordance.
 * @param {object} providerConfigs - User's configured provider keys.
 * @param {object} toolApiKeys - Map of tool_name → has_key boolean.
 * @param {boolean} requireProviderKeys - When true, agent nodes require
 *   a configured provider; when false (beta) stackform defaults suffice.
 */
function NodePalette({
  onAddNode,
  isMobile,
  providerConfigs,
  toolApiKeys,
  requireProviderKeys,
  nodeCount,
  maxNodes,
}) {
  const onDragStart = (e, nodeType) => {
    e.dataTransfer.setData('application/wf-node-type', nodeType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const atLimit = nodeCount >= maxNodes;
  const hasProvider = Object.keys(providerConfigs || {}).length > 0;
  const hasAnyTool  = Object.values(toolApiKeys || {}).some(Boolean);

  return (
    <>
      <div className="wf-sidebar-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Node Palette</span>
        <NodeCounterChip nodeCount={nodeCount} maxNodes={maxNodes} />
      </div>
      <div className="wf-palette">
        {PALETTE_ITEMS.map(({ nodeType, label, requiresProvider, requiresTool }) => {
          const disabled =
            atLimit ||
            (requiresProvider && requireProviderKeys && !hasProvider) ||
            (requiresTool && !hasAnyTool);

          const tooltip = atLimit
            ? `Node limit reached (${maxNodes}/${maxNodes}). Delete a node to add another.`
            : disabled
              ? requiresProvider
                ? 'A provider API key is required to use agent nodes. Add one in Settings → API Keys.'
                : 'A Tavily or You Search API key is required to use tool nodes. Add one in Settings → API Keys.'
              : isMobile
                ? `Tap to add a ${label} node`
                : `Drag to canvas or click to add a ${label} node`;

          return (
            <div
              key={nodeType}
              className={`wf-palette-node${isMobile ? ' wf-palette-node-mobile' : ''}${disabled ? ' wf-palette-node--disabled' : ''}`}
              draggable={!isMobile && !disabled}
              onDragStart={
                !isMobile && !disabled
                  ? (e) => onDragStart(e, nodeType)
                  : undefined
              }
              onClick={
                !disabled && onAddNode
                  ? () => onAddNode(nodeType)
                  : undefined
              }
              title={tooltip}
              aria-disabled={disabled}
            >
              <span
                className="wf-palette-dot"
                style={{ background: NODE_COLORS[nodeType] }}
              />
              {label}
              {isMobile && !disabled && (
                <span className="wf-palette-node-add-badge">+</span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ─── Canvas actions menu & floating action bar ──────────────── */

/**
 * Dropdown menu for canvas actions on mobile (tap ⋮ to expand).
 */
function CanvasActionsMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="wf-canvas-actions-menu">
      <button
        className="wf-btn wf-canvas-btn"
        onClick={() => setOpen((o) => !o)}
        title="Workflow actions"
      >
        ⋮
      </button>
      {open && (
        <div className="wf-canvas-actions-dropdown">
          {items.map((item) =>
            item.divider ? (
              <div
                key={item.key}
                style={{
                  height: 1,
                  background: 'var(--border-color)',
                  margin: '4px 0',
                }}
              />
            ) : (
              <button
                key={item.key || item.label}
                className="wf-canvas-dropdown-item"
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick();
                    setOpen(false);
                  }
                }}
                disabled={item.disabled}
              >
                {item.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Floating action bar overlaid on the canvas (top-right corner).
 *
 * On desktop renders Save / Undo / Redo / Guide / Run as a row.
 * On mobile collapses them into a ⋮ dropdown to save space.
 */
function CanvasFloatingBar({
  activeWorkflowId,
  saving,
  onSave,
  saveMsg,
  canUndo,
  onUndo,
  canRedo,
  onRedo,
  onGuide,
  canRun,
  running,
  onRun,
  onCancel,
  runStatus,
  onDismissRun,
  onShowOutput,
  onClear,
  nodeCount,
  maxNodes,
  onCopy,
  onEdit,
  onDelete,
  isUserWorkflow,
}) {
  const actionItems = [
    {
      key: 'copy',
      label: '📋 Copy',
      onClick: onCopy,
      disabled: !activeWorkflowId,
    },
    ...(isUserWorkflow ? [
      {
        key: 'edit-wf',
        label: '✏️ Edit',
        onClick: onEdit,
        disabled: !activeWorkflowId,
      },
      {
        key: 'delete-wf',
        label: '🗑 Delete',
        onClick: onDelete,
        disabled: !activeWorkflowId,
      },
    ] : []),
    { key: 'div0', divider: true },
    {
      key: 'save',
      label: saving ? 'Saving…' : '💾 Save',
      onClick: onSave,
      disabled: !activeWorkflowId || saving,
    },
    { key: 'div1', divider: true },
    {
      key: 'undo',
      label: '↩ Undo',
      onClick: onUndo,
      disabled: !canUndo,
    },
    {
      key: 'redo',
      label: '↪ Redo',
      onClick: onRedo,
      disabled: !canRedo,
    },
    { key: 'div2', divider: true },
    {
      key: 'guide',
      label: '? Guide',
      onClick: onGuide,
      disabled: false,
    },
    {
      key: 'clear',
      label: '↺ Clear',
      onClick: onClear,
      disabled: !activeWorkflowId,
    },
    { key: 'div3', divider: true },
    running
      ? {
          key: 'cancel',
          label: '⏹ Cancel',
          onClick: onCancel,
          disabled: false,
        }
      : {
          key: 'run',
          label: '▶ Run',
          onClick: onRun,
          disabled: !canRun,
        },
  ];

  if (runStatus) {
    actionItems.push(
      { key: 'div4', divider: true },
      {
        key: 'output',
        label: 'Output',
        onClick: onShowOutput,
        disabled: false,
      },
      {
        key: 'dismiss',
        label: '× Dismiss',
        onClick: onDismissRun,
        disabled: false,
      },
    );
  }

  return (
    <div className="wf-canvas-actions">
      <div className="wf-canvas-actions-row">
        {activeWorkflowId && (
          <NodeCounterChip nodeCount={nodeCount} maxNodes={maxNodes} />
        )}
        {activeWorkflowId && (
          <>
            <button
              className="wf-btn wf-canvas-btn"
              onClick={onCopy}
              title="Copy"
            >
              <RiFileCopyLine size={14} />
            </button>
            {isUserWorkflow && (
              <>
                <button
                  className="wf-btn wf-canvas-btn"
                  onClick={onEdit}
                  title="Edit"
                >
                  <RiPencilLine size={14} />
                </button>
                <button
                  className="wf-btn wf-canvas-btn"
                  onClick={onDelete}
                  title="Delete"
                  style={{ color: 'var(--error-text-color, #dc2626)' }}
                >
                  <RiDeleteBinLine size={14} />
                </button>
              </>
            )}
          </>
        )}
        {saveMsg && (
          <span
            style={{
              fontSize: '0.75rem',
              color: saveMsg.error
                ? 'var(--error-text-color)'
                : 'var(--success-text-color)',
              whiteSpace: 'nowrap',
              padding: '0 4px',
            }}
          >
            {saveMsg.text}
          </span>
        )}
        <button
          className="wf-btn wf-canvas-btn wf-btn-primary"
          onClick={onSave}
          disabled={!activeWorkflowId || saving}
          title="Save (Ctrl+S)"
        >
          {saving ? 'Saving…' : '💾'}
        </button>
        <button
          className="wf-btn wf-canvas-btn"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          ↩
        </button>
        <button
          className="wf-btn wf-canvas-btn"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
        >
          ↪
        </button>
        <button
          className="wf-btn wf-canvas-btn"
          onClick={onGuide}
          title="Guide"
        >
          ?
        </button>
        <button
          className="wf-btn wf-canvas-btn"
          onClick={onClear}
          disabled={!activeWorkflowId}
          title="Clear canvas"
        >
          ↺
        </button>
        {running ? (
          <button
            className="wf-btn wf-canvas-btn wf-btn-danger"
            onClick={onCancel}
            title="Cancel running workflow"
          >
            ⏹
          </button>
        ) : (
          <button
            className="wf-btn wf-canvas-btn wf-btn-run"
            onClick={onRun}
            disabled={!canRun}
            title={canRun ? 'Run workflow' : 'Workflow must have input + output nodes'}
          >
            ▶
          </button>
        )}
        {runStatus && <RunStatusBadge runStatus={runStatus} />}
        {runStatus && (
          <button
            className="wf-btn wf-canvas-btn"
            onClick={onDismissRun}
            title="Dismiss run status"
            style={{ padding: '3px 7px', fontSize: '0.78rem' }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Main WorkflowBuilder page ─────────────────────────────── */

/**
 * Full-page workflow builder canvas.
 *
 * Renders independently of the main App layout. Accessed via the
 * /workflow-builder route. Uses @xyflow/react for the DAG canvas.
 */
function WorkflowBuilderInner() {
  const { theme, toggleTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  /* Auth context must be in scope so UserSettingsMenu renders. */
  useAuth();

  const API_URL = process.env.REACT_APP_AUTH_BACKEND_URL || '';

  const {
    workflows,
    loadingWorkflows,
    saving,
    activeRuns,
    error,
    listWorkflows,
    createWorkflow,
    getWorkflow,
    saveWorkflow,
    deleteWorkflow,
    runWorkflow,
    rerunWorkflow,
    cancelRun,
    stopStream,
    clearError,
    clearRunState,
  } = useWorkflow();

  const {
    personas,
    agentModelAssignments,
    providerConfigs,
    modelLists,
    activeProfileName,
    profiles,
    toolApiKeys,
    requireProviderKeys,
  } = useModelSettings();

  const [activeWorkflowId, setActiveWorkflowId] = useState(null);
  const [activeWorkflow, setActiveWorkflow] = useState(null);

  /* Derive single-workflow run state for the currently open workflow.
     Must come after activeWorkflowId useState declaration. */
  const runState = activeRuns[activeWorkflowId] || null;
  const running = !!(
    runState &&
    ['pending', 'running'].includes(runState.status)
  );
  const [workflowName, setWorkflowName] = useState('');
  // Pinned model profile for this workflow (null = use the active profile).
  const [workflowProfile, setWorkflowProfile] = useState(null);
  // Node model slots and their profile defaults, served by the backend.
  const { modelSlots, error: modelSlotsError } = useModelSlots(
    workflowProfile,
    activeProfileName,
  );
  const [nodes, setNodes, onNodesChangeRaw] = useNodesState([]);
  const [edges, setEdges, onEdgesChangeRaw] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [rightTab, setRightTab] = useState('config');
  const [saveMsg, setSaveMsg] = useState(null);

  /* ── Canvas-level workflow actions (copy / edit / delete) ── */
  const [copyPrefill, setCopyPrefill] = useState(null);
  const [canvasEditWf, setCanvasEditWf] = useState(null);
  const [canvasEditName, setCanvasEditName] = useState('');
  const [canvasEditDesc, setCanvasEditDesc] = useState('');
  const [canvasEditError, setCanvasEditError] = useState('');

  /* ── Panel collapse state ── */
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  /* ── Mobile detection ── */
  const isMobile = useIsMobile(1024);

  /* Workflow IDs with a server-side run in progress (any tab/session), so
     the sidebar "running" dot reflects server truth, not just this tab's
     locally-streamed runs. */
  const { runningWorkflowIds } = useUserRuns();
  const [mobileDismissed, setMobileDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('wf-mobile-dismissed') === '1';
    } catch {
      return false;
    }
  });
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showMobileOutput, setShowMobileOutput] = useState(false);
  const [showMobileConfig, setShowMobileConfig] = useState(false);
  const [showMobileHistory, setShowMobileHistory] = useState(false);
  // Lifted from WorkflowList so both the canvas button and sidebar "+" share
  // the same new-workflow creation form.
  const [showNewWorkflowForm, setShowNewWorkflowForm] = useState(false);

  /* Auto-open config sheet when a node is tapped on mobile. */
  /* Switch the right panel to Config whenever a node is selected on
   * desktop, matching mobile (which opens the config sheet on tap).
   * Selecting a node is always intentional — an empty-canvas click fires
   * onPaneClick, which clears the selection rather than focusing it — so
   * this never fires on an accidental click and never strands the user on
   * another tab when they pick a node.
   * Also called directly from onNodeClick so re-clicking the same node
   * (same reference) still switches the tab. */
  const handleNodeFocus = useCallback(() => {
    if (!isMobile) {
      setRightTab('config');
    } else {
      setShowMobileConfig(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (selectedNode) handleNodeFocus();
  }, [selectedNode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMobileDismiss = useCallback(() => {
    try {
      sessionStorage.setItem('wf-mobile-dismissed', '1');
    } catch {
      /* storage may be unavailable (private mode, quota) — non-fatal */
    }
    setMobileDismissed(true);
  }, []);

  /* ── Panel resize state ── */
  const [leftWidth, setLeftWidth] = useState(280);
  const [rightWidth, setRightWidth] = useState(300);
  const leftWidthRef = useRef(280);
  const rightWidthRef = useRef(300);

  useEffect(() => { leftWidthRef.current = leftWidth; }, [leftWidth]);
  useEffect(() => { rightWidthRef.current = rightWidth; }, [rightWidth]);

  const startLeftResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidthRef.current;
    const onMove = (ev) =>
      setLeftWidth(Math.max(280, Math.min(520, startW + (ev.clientX - startX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const startRightResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = rightWidthRef.current;
    const onMove = (ev) =>
      setRightWidth(Math.max(240, Math.min(700, startW - (ev.clientX - startX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const saveMsgTimerRef = useRef(null);
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);

  /* Close ALL SSE streams when the page unmounts to prevent leaks. */
  useEffect(() => () => stopStream(), [stopStream]);

  /* Clear the save-message auto-dismiss timer on unmount. */
  useEffect(() => () => {
    if (saveMsgTimerRef.current) clearTimeout(saveMsgTimerRef.current);
  }, []);

  /* Auto-switch right panel to Run Output when a run starts. */
  useEffect(() => {
    if (running) setRightTab('run');
  }, [running]);

  /* When switching to a workflow that already has a run, show the
     Run tab; otherwise fall back to Config (not History). */
  const activeRunsRef = useRef(activeRuns);
  useEffect(() => { activeRunsRef.current = activeRuns; }, [activeRuns]);
  useEffect(() => {
    if (!activeWorkflowId) {
      setRightTab('history'); // no workflow → show all-runs history
      return;
    }
    if (activeRunsRef.current[activeWorkflowId]) {
      setRightTab('run');
    } else {
      setRightTab((prev) => (prev === 'history' ? prev : 'config'));
    }
  }, [activeWorkflowId]);

  /* Reset right panel to Config when run state is dismissed. */
  useEffect(() => {
    if (!runState) setRightTab((prev) => prev === 'history' ? prev : 'config');
  }, [runState]);

  /* Sync latest nodes/edges into refs so history snapshots are current. */
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  /* Undo/redo history — the snapshot stack lives in the hook. */
  const {
    canUndo,
    canRedo,
    pushHistory,
    undo: handleUndo,
    redo: handleRedo,
    resetHistory,
    clearHistory,
    isApplyingRef,
  } = useUndoRedo({
    nodesRef,
    edgesRef,
    setNodes,
    setEdges,
    onRestore: () => setSelectedNode(null),
  });

  /* Wrap onNodesChange to push history before Delete-key removals and
     clear selectedNode when its node is removed. Must come after
     pushHistory is defined. */
  const onNodesChange = useCallback(
    (changes) => {
      const removedIds = new Set(
        changes
          .filter((c) => c.type === 'remove')
          .map((c) => c.id),
      );
      if (removedIds.size > 0) {
        pushHistory();
        setSelectedNode((prev) =>
          prev && removedIds.has(prev.id) ? null : prev,
        );
      }
      onNodesChangeRaw(changes);
    },
    [onNodesChangeRaw, pushHistory],
  );

  /* Wrap onEdgesChange to push history before Delete-key edge removals
     and clear output node variable_name when its incoming edge is gone.
     removedTargets must be computed from edgesRef BEFORE onEdgesChangeRaw
     runs — the xyflow state updater immediately processes removals, so any
     `setEdges(currentEdges => ...)` callback would receive a list that no
     longer contains the deleted edges. */
  const onEdgesChange = useCallback(
    (changes) => {
      const removes = changes.filter((c) => c.type === 'remove');
      if (removes.length > 0 && !isApplyingRef.current) {
        pushHistory();
      }
      if (removes.length > 0) {
        const removedIds = new Set(removes.map((c) => c.id));
        /* Read the ref synchronously — it still holds the pre-removal edges. */
        const removedTargets = new Set(
          edgesRef.current
            .filter((e) => removedIds.has(e.id))
            .map((e) => e.target),
        );
        onEdgesChangeRaw(changes);
        if (removedTargets.size > 0) {
          setNodes((nds) =>
            nds.map((n) => {
              if (
                removedTargets.has(n.id) &&
                n.data?.config?.node_type === 'output' &&
                /* Only clear if no surviving edge still targets this node. */
                !edgesRef.current.some(
                  (e) => e.target === n.id && !removedIds.has(e.id),
                )
              ) {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    config: { ...n.data.config, variable_name: '' },
                  },
                };
              }
              return n;
            }),
          );
        }
      } else {
        onEdgesChangeRaw(changes);
      }
    },
    [onEdgesChangeRaw, pushHistory, setNodes, isApplyingRef],
  );

  /* Load workflow list on mount */
  useEffect(() => {
    listWorkflows();
  }, [listWorkflows]);

  /* Load a workflow into the canvas */
  const loadWorkflow = useCallback(
    async (workflowId) => {
      const wf = await getWorkflow(workflowId);
      if (!wf) return;
      setActiveWorkflow(wf);
      setActiveWorkflowId(workflowId);
      setWorkflowName(wf.name || '');
      setWorkflowProfile(wf.dag_json?.profile_name || null);
      setSelectedNode(null);
      const { nodes: fn, edges: fe } = dagToFlow(wf.dag_json || {});
      setNodes(fn);
      setEdges(fe);
      resetHistory(fn, fe);
    },
    [getWorkflow, setNodes, setEdges, resetHistory],
  );

  /* Auto-load a workflow specified via ?load=<workflowId> URL param.
   * Runs once on mount.  Cleans the param from the URL so a page
   * refresh doesn't re-trigger the load. */
  useEffect(() => {
    const targetId = searchParams.get('load');
    if (!targetId) return;
    // Reject non-UUID values so a crafted URL cannot probe arbitrary paths.
    if (!/^[0-9a-f-]{36}$/i.test(targetId)) return;
    setSearchParams(prev => { prev.delete('load'); return prev; }, { replace: true });
    loadWorkflow(targetId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearWorkflow = useCallback(() => {
    // Cancel any in-progress run so it doesn't become unreachable
    // from the UI after the workflow is deselected.
    const wfId = activeWorkflowId;
    if (wfId) {
      const run = activeRuns[wfId];
      if (run && ['pending', 'running'].includes(run.status)) {
        cancelRun(wfId, run.runId || null);
        stopStream();
        clearRunState(wfId);
      }
    }
    setActiveWorkflow(null);
    setActiveWorkflowId(null);
    setWorkflowName('');
    setWorkflowProfile(null);
    setSelectedNode(null);
    setNodes([]);
    setEdges([]);
    clearHistory();
  }, [activeWorkflowId, activeRuns, cancelRun, stopStream, clearRunState, setNodes, setEdges, clearHistory]);

  const handleSelectWorkflow = useCallback(
    (workflowId) => {
      if (workflowId === activeWorkflowId) {
        clearWorkflow(); // clicking the active workflow deselects it
        return;
      }
      loadWorkflow(workflowId);
    },
    [activeWorkflowId, loadWorkflow, clearWorkflow],
  );

  const handleNewWorkflow = useCallback(
    async (name, description = '', icon = null, dagJson = null) => {
      const wfName = name || `Workflow ${new Date().toLocaleDateString()}`;
      const result = await createWorkflow(wfName, description, icon, dagJson);
      if (!result) return;
      await listWorkflows();
      await loadWorkflow(result.workflow_id);
    },
    [createWorkflow, listWorkflows, loadWorkflow],
  );

  const handleDeleteWorkflow = useCallback(
    async (workflowId) => {
      if (
        !window.confirm(
          'Delete this workflow? This cannot be undone.',
        )
      )
        return;
      await deleteWorkflow(workflowId);
      clearRunState(workflowId);
      if (workflowId === activeWorkflowId) {
        setActiveWorkflowId(null);
        setActiveWorkflow(null);
        setWorkflowName('');
        setWorkflowProfile(null);
        setNodes([]);
        setEdges([]);
        setSelectedNode(null);
      }
    },
    [
      activeWorkflowId,
      deleteWorkflow,
      setNodes,
      setEdges,
      clearRunState,
    ],
  );

  const showSaveMessage = useCallback((msg, isError = false) => {
    setSaveMsg({ text: msg, error: isError });
    if (saveMsgTimerRef.current) clearTimeout(saveMsgTimerRef.current);
    saveMsgTimerRef.current = setTimeout(() => setSaveMsg(null), 3000);
  }, []);

  /** Open the canvas-level copy pre-fill form for the active workflow. */
  const handleCanvasCopy = useCallback(() => {
    if (!activeWorkflow) return;
    setCopyPrefill({
      name: `${activeWorkflow.name} (copy)`,
      desc: activeWorkflow.description || '',
      dagJson: activeWorkflow.dag_json,
    });
    if (isMobile) setShowMobileSidebar(true);
  }, [activeWorkflow, isMobile, setShowMobileSidebar]);

  /** Open the canvas-level edit modal for the active workflow. */
  const handleCanvasEditActive = useCallback(() => {
    if (!activeWorkflow) return;
    setCanvasEditWf(activeWorkflow);
    setCanvasEditName(activeWorkflow.name);
    setCanvasEditDesc(activeWorkflow.description || '');
    setCanvasEditError('');
  }, [activeWorkflow]);

  /**
   * Rename a workflow (name-only PATCH — does not touch the DAG).
   * Updates local workflowName state when the active workflow is renamed.
   */
  const handleRenameWorkflow = useCallback(
    async (workflowId, newName, newDescription) => {
      const ok = await saveWorkflow(workflowId, {
        name: newName,
        ...(newDescription !== undefined && { description: newDescription }),
      });
      if (ok) {
        if (workflowId === activeWorkflowId) {
          setWorkflowName(newName);
        }
        await listWorkflows();
        showSaveMessage('Saved ✓');
      } else {
        showSaveMessage('Save failed', true);
      }
    },
    [saveWorkflow, activeWorkflowId, listWorkflows, showSaveMessage],
  );

  /** Commit the canvas-level edit modal — must follow handleRenameWorkflow. */
  const commitCanvasEdit = useCallback(() => {
    const name = canvasEditName.trim();
    const desc = canvasEditDesc.trim();
    if (!name) { setCanvasEditError('Name is required.'); return; }
    if (!desc) { setCanvasEditError('Description is required.'); return; }
    handleRenameWorkflow(canvasEditWf.id, name, desc);
    setCanvasEditWf(null);
  }, [canvasEditName, canvasEditDesc, canvasEditWf, handleRenameWorkflow]);

  const handleSave = useCallback(async () => {
    if (!activeWorkflowId) return;
    const dag = flowToDag(nodes, edges, workflowProfile);
    const ok = await saveWorkflow(activeWorkflowId, {
      name: workflowName,
      dag_json: dag,
    });
    if (ok) {
      showSaveMessage('Saved ✓');
    } else {
      showSaveMessage('Save failed', true);
    }
  }, [
    activeWorkflowId,
    nodes,
    edges,
    workflowName,
    workflowProfile,
    saveWorkflow,
    showSaveMessage,
  ]);

  /* Save on Ctrl+S, undo Ctrl+Z, redo Ctrl+Y / Ctrl+Shift+Z */
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (activeWorkflowId) handleSave();
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'z' &&
        !e.shiftKey
      ) {
        e.preventDefault();
        handleUndo();
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.key === 'z' && e.shiftKey))
      ) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeWorkflowId, handleSave, handleUndo, handleRedo]);

  const handleRunClick = useCallback(async () => {
    if (!activeWorkflowId) return;
    const inputNodes = nodes.filter(
      (n) => n.data?.config?.node_type === 'input',
    );
    if (inputNodes.length > 0) {
      setShowRunDialog(true);
    } else {
      /* No input nodes — run immediately with empty inputs */
      await runWorkflow(activeWorkflowId, {});
    }
  }, [activeWorkflowId, nodes, runWorkflow]);

  const handleRunConfirm = useCallback(
    async (inputValues) => {
      setShowRunDialog(false);
      await runWorkflow(activeWorkflowId, inputValues);
    },
    [activeWorkflowId, runWorkflow],
  );

  const handleCancelClick = useCallback(
    (runId) => {
      if (!activeWorkflowId) return;
      /* runId is provided by LiveRunOutput; fall back to current run
         when called without args (e.g. from CanvasFloatingBar).
         Always call cancelRun even when id is null so that
         pendingCancelRef is queued during the pending phase. */
      const id =
        runId || activeRunsRef.current[activeWorkflowId]?.runId;
      cancelRun(activeWorkflowId, id || null);
    },
    [activeWorkflowId, cancelRun],
  );

  const handleNodeUpdate = useCallback(
    (nodeId, { label, config }) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, label, config } }
            : n,
        ),
      );
      setSelectedNode((prev) =>
        prev?.id === nodeId
          ? { ...prev, data: { ...prev.data, label, config } }
          : prev,
      );
    },
    [setNodes],
  );

  const handleNodeDelete = useCallback(
    (nodeId) => {
      pushHistory();
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) =>
        eds.filter(
          (e) => e.source !== nodeId && e.target !== nodeId,
        ),
      );
      setSelectedNode(null);
    },
    [setNodes, setEdges, pushHistory],
  );

  /**
   * Programmatically add a node of the given type at a stacked-grid
   * position.  Used for mobile tap-to-add (touch devices cannot use
   * the dataTransfer drag API) and as a desktop click-to-add shortcut.
   *
   * Positions nodes in a 3-column grid starting at (80, 80) with
   * 200 px horizontal and 150 px vertical spacing so that rapidly
   * added nodes never stack on top of each other.
   */
  const handleAddNode = useCallback(
    (nodeType) => {
      if (nodesRef.current.length >= MAX_WORKFLOW_NODES) return;
      pushHistory();
      const count = nodesRef.current.length;
      const col = count % 3;
      const row = Math.floor(count / 3);
      const position = {
        x: 80 + col * 200,
        y: 80 + row * 150,
      };
      const id = `node-${crypto.randomUUID().slice(0, 8)}`;
      const newNode = {
        id,
        type: `${nodeType}Node`,
        position,
        data: {
          label: NODE_TYPE_LABELS[nodeType] || nodeType,
          config: defaultConfig(nodeType),
        },
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [pushHistory, setNodes],
  );

  const inputNodes = useMemo(
    () => nodes.filter((n) => n.data?.config?.node_type === 'input'),
    [nodes],
  );

  const canRun =
    !!activeWorkflowId &&
    !running &&
    nodes.some((n) => n.data?.config?.node_type === 'input') &&
    nodes.some((n) => n.data?.config?.node_type === 'output');

  /* Action items for the mobile ⋮ menu — always rendered on mobile. */
  const mobileActionItems = useMemo(() => {
    const isUserWf = activeWorkflow && !activeWorkflow.is_default;
    const items = [
      {
        key: 'save',
        label: saving ? 'Saving…' : '💾 Save',
        onClick: handleSave,
        disabled: !activeWorkflowId || saving,
      },
      { key: 'div1', divider: true },
      { key: 'undo', label: '↩ Undo', onClick: handleUndo, disabled: !canUndo },
      { key: 'redo', label: '↪ Redo', onClick: handleRedo, disabled: !canRedo },
      { key: 'div2', divider: true },
      { key: 'guide', label: '? Guide', onClick: () => setShowGuide(true), disabled: false },
      { key: 'clear', label: '↺ Clear Canvas', onClick: clearWorkflow, disabled: !activeWorkflowId },
      {
        key: 'history',
        label: '🕐 History',
        onClick: () => setShowMobileHistory(true),
        disabled: false,
      },
      { key: 'div3', divider: true },
      {
        key: 'run',
        label: running ? '⏳ Running…' : '▶ Run',
        onClick: handleRunClick,
        disabled: !canRun,
      },
    ];
    if (activeWorkflowId) {
      items.push(
        { key: 'div-wf', divider: true },
        {
          key: 'copy-wf',
          label: '📋 Copy',
          onClick: handleCanvasCopy,
          disabled: false,
        },
      );
      if (isUserWf) {
        items.push(
          {
            key: 'edit-wf',
            label: '✏️ Edit',
            onClick: handleCanvasEditActive,
            disabled: false,
          },
          {
            key: 'delete-wf',
            label: '🗑 Delete',
            onClick: () => handleDeleteWorkflow(activeWorkflowId),
            disabled: false,
          },
        );
      }
    }
    if (selectedNode) {
      items.push(
        { key: 'div-cfg', divider: true },
        {
          key: 'configure',
          label: '⚙ Config',
          onClick: () => setShowMobileConfig(true),
          disabled: false,
        },
      );
    }
    if (runState) {
      items.push(
        { key: 'div4', divider: true },
        {
          key: 'output',
          label: '▤ Output',
          onClick: () => setShowMobileOutput(true),
          disabled: false,
        },
        {
          key: 'dismiss',
          label: '× Dismiss Output',
          onClick: () => {
            clearRunState(activeWorkflowId);
            setShowMobileOutput(false);
          },
          disabled: false,
        },
      );
    }
    return items;
  }, [
    saving, handleSave, activeWorkflowId, activeWorkflow,
    handleUndo, canUndo, handleRedo, canRedo,
    running, handleRunClick, canRun,
    selectedNode,
    runState, clearRunState, setShowMobileOutput,
    setShowMobileHistory, setShowMobileConfig, clearWorkflow,
    handleCanvasCopy, handleCanvasEditActive, handleDeleteWorkflow,
  ]);

  /*
   * Build a node-id → status map from the latest poll result.
   * During a run we also infer which node is currently "active":
   * any node not yet in node_results_json whose every predecessor
   * is already completed.  This lets edges and nodes reflect live
   * progress between the 2-second poll ticks.
   */
  const nodeStatuses = useMemo(() => {
    const results = runState?.node_results_json;
    if (!results && !running) return {};

    const statuses = {};
    if (results) {
      for (const [nid, r] of Object.entries(results)) {
        statuses[nid] = r.status;
      }
    }

    if (running) {
      for (const node of nodes) {
        if (statuses[node.id]) continue;
        const predIds = edges
          .filter((e) => e.target === node.id)
          .map((e) => e.source);
        const allDone =
          predIds.length === 0 ||
          predIds.every((p) => statuses[p] === 'completed');
        if (allDone) statuses[node.id] = 'active';
      }
    }

    return statuses;
  }, [runState, running, nodes, edges]);

  return (
    <div className="wf-page">
      {/* ── Mobile warning overlay ── */}
      {isMobile && !mobileDismissed && (
        <div className="wf-mobile-overlay">
          <div className="wf-mobile-card">
            <div className="wf-mobile-icon">🖥️</div>
            <h2 className="wf-mobile-title">Best on Desktop</h2>
            <p className="wf-mobile-body">
              The Workflow Builder is designed for mouse and keyboard.
              On a small screen you won't be able to drag nodes or
              edit workflow configurations.
            </p>
            <button
              className="wf-btn wf-btn-primary wf-mobile-cta"
              onClick={handleMobileDismiss}
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}

      {/* ── Error banner ── */}
      {error && (
        <div
          style={{
            background: '#fee2e2',
            color: '#991b1b',
            padding: '6px 16px',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          {/* pre-line: a refused run lists one failing node per line. */}
          <span style={{ flex: 1, whiteSpace: 'pre-line' }}>{error}</span>
          <button
            onClick={clearError}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#991b1b',
              fontSize: '1rem',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Body ── */}
      <NodeStatusCtx.Provider value={{ nodeStatuses, isRunning: running }}>
      <div className="wf-body">
        {/* Left sidebar — desktop only */}
        {!isMobile && (
          <>
            <div
              className={`wf-sidebar resizable${leftCollapsed ? ' wf-panel-collapsed' : ''}`}
              style={{
                width: leftCollapsed ? 36 : leftWidth,
                overflow: 'visible',
              }}
            >
              {/* PanelShell owns the collapse toggle, header, footer, and About popup */}
              <PanelShell
                collapsed={leftCollapsed}
                onToggleCollapse={() => setLeftCollapsed(c => !c)}
                theme={theme}
                toggleTheme={toggleTheme}
                API_URL={API_URL}
              >
                {/* Flex column fills space between header and footer.
                    WorkflowList scrolls; NodePalette pins to the bottom.
                    width:100% is explicit on both wrappers to satisfy
                    Chrome/Safari, which don't reliably apply align-self:stretch
                    inside overflow:visible flex containers. */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  minHeight: 0,
                  width: '100%',
                }}>
                  <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, width: '100%' }}>
                    <WorkflowList
                      workflows={workflows}
                      loadingWorkflows={loadingWorkflows}
                      activeWorkflowId={activeWorkflowId}
                      activeRuns={activeRuns}
                      runningWorkflowIds={runningWorkflowIds}
                      onSelect={handleSelectWorkflow}
                      onNew={handleNewWorkflow}
                      onDelete={handleDeleteWorkflow}
                      onRename={handleRenameWorkflow}
                      showNewForm={showNewWorkflowForm}
                      setShowNewForm={setShowNewWorkflowForm}
                      copyPrefill={copyPrefill}
                      onCopyPrefillConsumed={() => setCopyPrefill(null)}
                    />
                  </div>
                  {activeWorkflowId && (
                    <NodePalette
                      isMobile={false}
                      onAddNode={handleAddNode}
                      providerConfigs={providerConfigs}
                      toolApiKeys={toolApiKeys}
                      requireProviderKeys={requireProviderKeys}
                      nodeCount={nodes.length}
                      maxNodes={MAX_WORKFLOW_NODES}
                    />
                  )}
                </div>
              </PanelShell>
            </div>

            {/* Left resize handle — hidden when collapsed */}
            {!leftCollapsed && (
              <div
                className="wf-resize-handle"
                onMouseDown={startLeftResize}
                title="Drag to resize sidebar"
              />
            )}
          </>
        )}

        {/* Canvas area wrapper — always rendered; mobile overlay buttons live here */}
        <div className="wf-canvas-area">
          {/* Mobile hamburger (top-left) and ⋮ menu (top-right) — always visible */}
          {isMobile && (
            <>
              <button
                className="wf-canvas-menu-btn"
                onClick={() => setShowMobileSidebar(true)}
                title="Workflows & Nodes"
                aria-label="Open workflow list"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="3" y1="6"  x2="21" y2="6"  />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              {activeWorkflowId && (
                <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)' }}>
                  <NodeCounterChip nodeCount={nodes.length} maxNodes={MAX_WORKFLOW_NODES} />
                </div>
              )}
              <div className="wf-canvas-actions">
                <CanvasActionsMenu items={mobileActionItems} />
              </div>
            </>
          )}

          {/* Toolbar — always rendered on desktop, disabled when no workflow selected */}
          {!isMobile && (
            <CanvasFloatingBar
              activeWorkflowId={activeWorkflowId}
              saving={saving}
              onSave={handleSave}
              saveMsg={saveMsg}
              canUndo={canUndo}
              onUndo={handleUndo}
              canRedo={canRedo}
              onRedo={handleRedo}
              onGuide={() => setShowGuide(true)}
              onClear={clearWorkflow}
              canRun={canRun}
              running={running}
              onRun={handleRunClick}
              onCancel={handleCancelClick}
              runStatus={runState}
              onDismissRun={() => {
                clearRunState(activeWorkflowId);
                setShowMobileOutput(false);
              }}
              onShowOutput={() => setShowMobileOutput(true)}
              nodeCount={nodes.length}
              maxNodes={MAX_WORKFLOW_NODES}
              onCopy={handleCanvasCopy}
              onEdit={handleCanvasEditActive}
              onDelete={() => handleDeleteWorkflow(activeWorkflowId)}
              isUserWorkflow={!!(activeWorkflow && !activeWorkflow.is_default)}
            />
          )}

          {activeWorkflowId ? (
            <CanvasInner
              activeWorkflow={activeWorkflow}
              activeWorkflowId={activeWorkflowId}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              setNodes={setNodes}
              setEdges={setEdges}
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNode}
              onNodeUpdate={handleNodeUpdate}
              onNodeDelete={handleNodeDelete}
              pushHistory={pushHistory}
              floatingBar={null}
              onNodeFocus={handleNodeFocus}
            />
          ) : (
            <div className="wf-canvas-wrap wf-canvas-empty">
              <div style={{ fontSize: '2.5rem' }}>🔧</div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                Workflow Builder
              </div>
              <div className="wf-canvas-empty-desc">
                Select a workflow from the sidebar or create a new one to
                get started.
              </div>
              <button
                className="wf-btn wf-btn-primary"
                style={{ marginTop: 8 }}
                onClick={() => {
                  if (isMobile) setShowMobileSidebar(true);
                  setShowNewWorkflowForm(true);
                }}
              >
                + New Workflow
              </button>
            </div>
          )}
        </div>

        {/* Right resize handle — hidden when collapsed */}
        {!isMobile && !rightCollapsed && (
          <div
            className="wf-resize-handle"
            onMouseDown={startRightResize}
            title="Drag to resize panel"
          />
        )}

        {/* Right config panel — desktop only */}
        {!isMobile && (
          <div
            className={`wf-config-panel resizable${rightCollapsed ? ' wf-panel-collapsed' : ''}`}
            style={{
              width: rightCollapsed ? 36 : rightWidth,
              overflow: 'visible',
            }}
          >
            {/* Collapse / expand toggle — absolutely positioned at left edge */}
            <button
              className="wf-panel-toggle wf-panel-toggle-right"
              onClick={() => setRightCollapsed(c => !c)}
              title={rightCollapsed ? 'Expand config panel' : 'Collapse config panel'}
            >
              {rightCollapsed ? '‹' : '›'}
            </button>

            {/* Inner content wrapper owns the overflow clipping */}
            {!rightCollapsed && (
              <div style={{
                overflow: 'hidden',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}>
                {/* Tab bar — always visible; Config and Run only when a workflow is open */}
                <div
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid var(--border-color)',
                    flexShrink: 0,
                    background: 'var(--panel-background)',
                  }}
                >
                  {(activeWorkflowId ? ['config', 'run', 'history'] : ['history']).map((id) => {
                    let label = 'Config';
                    if (id === 'run') {
                      label = running ? '⏳ Output' : 'Output';
                    } else if (id === 'history') {
                      label = activeWorkflowId ? 'History' : 'All Runs';
                    }
                    return (
                      <button
                        key={id}
                        onClick={() => setRightTab(id)}
                        style={{
                          flex: 1,
                          padding: '7px 4px',
                          fontSize: '0.76rem',
                          fontWeight: rightTab === id ? 700 : 400,
                          border: 'none',
                          borderBottom: rightTab === id
                            ? '2px solid var(--accent-color, #3b82f6)'
                            : '2px solid transparent',
                          background: 'transparent',
                          color: rightTab === id
                            ? 'var(--text-color)'
                            : 'var(--text-secondary)',
                          cursor: 'pointer',
                          borderRadius: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab content */}
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {rightTab === 'history' ? (
                    <WorkflowRunHistory
                      workflowId={activeWorkflowId}
                      nodes={nodes}
                      onRerun={(wfId, runId) =>
                        rerunWorkflow(wfId, runId, () => setRightTab('run'))
                      }
                      onStop={cancelRun}
                      onSelectWorkflow={!activeWorkflowId ? (wfId) => {
                        loadWorkflow(wfId);
                        setRightTab('config');
                      } : undefined}
                      running={running}
                      isMobile={false}
                    />
                  ) : rightTab === 'run' ? (
                    <>
                      {runState ? (
                        <LiveRunOutput
                          runState={runState}
                          dagNodes={nodes}
                          dagEdges={edges}
                          running={running}
                          onCancel={handleCancelClick}
                          onDismiss={() => clearRunState(activeWorkflowId)}
                        />
                      ) : (
                        <div
                          style={{
                            padding: '24px 16px',
                            fontSize: '0.82rem',
                            color: 'var(--text-secondary)',
                            textAlign: 'center',
                            lineHeight: 1.7,
                          }}
                        >
                          No output available.
                          <br />
                          Run the workflow to see results here.
                        </div>
                      )}
                    </>
                  ) : (
                    <WorkflowNodeConfig
                      node={selectedNode}
                      onUpdate={handleNodeUpdate}
                      onDelete={handleNodeDelete}
                      allNodes={nodes}
                      edges={edges}
                      personas={personas}
                      agentModelAssignments={agentModelAssignments}
                      providerConfigs={providerConfigs}
                      modelLists={modelLists}
                      activeProfileName={activeProfileName}
                      profiles={profiles}
                      workflowProfile={workflowProfile}
                      onWorkflowProfileChange={setWorkflowProfile}
                      modelSlots={modelSlots}
                      modelSlotsError={modelSlotsError}
                      toolApiKeys={toolApiKeys}
                      requireProviderKeys={requireProviderKeys}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </NodeStatusCtx.Provider>

      {/* Mobile sidebar drawer */}
      {isMobile && showMobileSidebar && (
        <div className="wf-sidebar-drawer">
          <div
            className="wf-sidebar-drawer-backdrop"
            onClick={() => setShowMobileSidebar(false)}
          />
          <div className="wf-sidebar-drawer-panel">
            {/* PanelShell owns the header, footer, and About popup */}
            <PanelShell
              onClose={() => setShowMobileSidebar(false)}
              theme={theme}
              toggleTheme={toggleTheme}
              API_URL={API_URL}
            >
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                width: '100%',
              }}>
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  WebkitOverflowScrolling: 'touch',
                  width: '100%',
                }}>
                  <WorkflowList
                    workflows={workflows}
                    loadingWorkflows={loadingWorkflows}
                    activeWorkflowId={activeWorkflowId}
                    activeRuns={activeRuns}
                    runningWorkflowIds={runningWorkflowIds}
                    isMobile={true}
                    onSelect={(id) => {
                      if (id !== activeWorkflowId) loadWorkflow(id);
                      setShowMobileSidebar(false);
                    }}
                    onNew={handleNewWorkflow}
                    onDelete={handleDeleteWorkflow}
                    onRename={handleRenameWorkflow}
                    showNewForm={showNewWorkflowForm}
                    setShowNewForm={setShowNewWorkflowForm}
                    copyPrefill={copyPrefill}
                    onCopyPrefillConsumed={() => setCopyPrefill(null)}
                  />
                </div>
                {activeWorkflowId && (
                  <NodePalette
                    isMobile={true}
                    onAddNode={(nodeType) => {
                      handleAddNode(nodeType);
                      setShowMobileSidebar(false);
                    }}
                    providerConfigs={providerConfigs}
                    toolApiKeys={toolApiKeys}
                    requireProviderKeys={requireProviderKeys}
                    nodeCount={nodes.length}
                    maxNodes={MAX_WORKFLOW_NODES}
                  />
                )}
              </div>
            </PanelShell>
          </div>
        </div>
      )}

      {/* Mobile output bottom sheet */}
      {isMobile && showMobileOutput && runState && (
        <div className="wf-output-sheet">
          <div className="wf-output-sheet-header">
            <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>
              Output
            </span>
            <button
              className="wf-btn"
              style={{ padding: '3px 8px', fontSize: '0.85rem' }}
              onClick={() => setShowMobileOutput(false)}
            >
              ×
            </button>
          </div>
          <div className="wf-output-sheet-body">
            <LiveRunOutput
              runState={runState}
              dagNodes={nodes}
              dagEdges={edges}
              running={running}
              onCancel={handleCancelClick}
              onDismiss={() => {
                clearRunState(activeWorkflowId);
                setShowMobileOutput(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Mobile node config bottom sheet */}
      {isMobile && showMobileConfig && selectedNode && (
        <div className="wf-config-sheet">
          <div
            className="wf-sidebar-drawer-backdrop"
            onClick={() => {
              setShowMobileConfig(false);
              setSelectedNode(null);
            }}
          />
          <div className="wf-config-sheet-panel">
            <div className="wf-config-sheet-header">
              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                Config
              </span>
              <button
                className="wf-btn"
                style={{ padding: '3px 8px', fontSize: '0.85rem' }}
                onClick={() => {
                  setShowMobileConfig(false);
                  setSelectedNode(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="wf-config-sheet-body">
              <WorkflowNodeConfig
                node={selectedNode}
                onUpdate={handleNodeUpdate}
                onDelete={(nodeId) => {
                  handleNodeDelete(nodeId);
                  setShowMobileConfig(false);
                }}
                allNodes={nodes}
                edges={edges}
                personas={personas}
                agentModelAssignments={agentModelAssignments}
                providerConfigs={providerConfigs}
                modelLists={modelLists}
                activeProfileName={activeProfileName}
                profiles={profiles}
                workflowProfile={workflowProfile}
                onWorkflowProfileChange={setWorkflowProfile}
                modelSlots={modelSlots}
                modelSlotsError={modelSlotsError}
                toolApiKeys={toolApiKeys}
                requireProviderKeys={requireProviderKeys}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile history bottom sheet */}
      {isMobile && showMobileHistory && (
        <div className="wf-config-sheet">
          <div
            className="wf-sidebar-drawer-backdrop"
            onClick={() => setShowMobileHistory(false)}
          />
          <div className="wf-config-sheet-panel">
            <div className="wf-config-sheet-header">
              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                {activeWorkflowId ? 'History' : 'All Runs'}
              </span>
              <button
                className="wf-btn"
                style={{ padding: '3px 8px', fontSize: '0.85rem' }}
                onClick={() => setShowMobileHistory(false)}
              >
                ×
              </button>
            </div>
            <div className="wf-config-sheet-body">
              <WorkflowRunHistory
                workflowId={activeWorkflowId}
                nodes={nodes}
                onRerun={(wfId, runId) => {
                  setShowMobileHistory(false);
                  rerunWorkflow(wfId, runId, () => setShowMobileOutput(true));
                }}
                onStop={cancelRun}
                onSelectWorkflow={!activeWorkflowId ? (wfId) => {
                  setShowMobileHistory(false);
                  loadWorkflow(wfId);
                } : undefined}
                running={running}
                isMobile={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Run input dialog */}
      {showRunDialog && (
        <RunInputDialog
          inputNodes={inputNodes}
          onConfirm={handleRunConfirm}
          onCancel={() => setShowRunDialog(false)}
        />
      )}

      {/* Workflow builder guide modal */}
      {showGuide && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            overflowY: 'auto',
            padding: '20px 12px 40px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowGuide(false);
          }}
        >
          <div
            style={{
              background: 'var(--modal-background)',
              border: '1px solid var(--border-color)',
              borderRadius: 10,
              padding: '28px 24px',
              width: '100%',
              maxWidth: 700,
              boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowGuide(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 16,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.4rem',
                color: 'var(--text-secondary)',
                lineHeight: 1,
              }}
              title="Close"
            >
              ×
            </button>
            <WorkflowTutorialContent onClose={() => setShowGuide(false)} />
          </div>
        </div>
      )}

      {/* ── Canvas-level edit modal — portalled to document.body ── */}
      {canvasEditWf && createPortal(
        <div
          className="wf-modal-backdrop"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: 'clamp(20px, 8vh, 80px) 20px 20px',
            overflowY: 'auto',
          }}
          onClick={() => setCanvasEditWf(null)}
        >
          <div
            className="wf-modal-card"
            style={{
              background: 'var(--modal-background)',
              border: '1px solid var(--border-color)',
              borderRadius: 10,
              padding: '20px',
              width: '100%', maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 14 }}>
              Edit Workflow
            </div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              Name <span style={{ color: 'var(--error-text-color, #dc2626)' }}>*</span>
            </label>
            <input
              className="wf-rename-input"
              value={canvasEditName}
              onChange={(e) => { setCanvasEditName(e.target.value); setCanvasEditError(''); }}
              placeholder="Workflow name"
              autoFocus={!isMobile}
              onKeyDown={(e) => { if (e.key === 'Escape') setCanvasEditWf(null); }}
              style={{ width: '100%', marginBottom: 10 }}
            />
            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              Description <span style={{ color: 'var(--error-text-color, #dc2626)' }}>*</span>
            </label>
            <textarea
              className="wf-rename-input"
              value={canvasEditDesc}
              onChange={(e) => { setCanvasEditDesc(e.target.value); setCanvasEditError(''); }}
              placeholder="Short description of what this workflow does."
              rows={3}
              style={{
                width: '100%', marginBottom: 6,
                resize: 'vertical', fontFamily: 'inherit', fontSize: '0.82rem',
              }}
            />
            {canvasEditError && (
              <div style={{ fontSize: '0.72rem', color: 'var(--error-text-color, #dc2626)', marginBottom: 8 }}>
                {canvasEditError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button className="wf-btn" onClick={() => setCanvasEditWf(null)}>
                Cancel
              </button>
              <button className="wf-btn wf-btn-primary" onClick={commitCanvasEdit}>
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

    </div>
  );
}

/**
 * Workflow Builder page wrapped in ReactFlowProvider.
 *
 * ReactFlowProvider is required for useReactFlow() to work inside
 * CanvasInner. The provider must wrap the entire component tree that
 * uses the ReactFlow context, not just the ReactFlow canvas element.
 */
export default function WorkflowBuilder() {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner />
    </ReactFlowProvider>
  );
}
